import React from 'react';
import type {
  WidgetPart,
  CTAWidgetPayload,
  ActionEvent,
  WidgetData,
} from '../types';
import CTAWidget from './cta-widget';
import {ListWidget} from './list-widget';
import {TitleWidget} from './title-widget';
import {PromptListWidget} from './prompt-list-widget';
import {PreviewCardWidget} from './preview-card-widget';
import {ActionCardWidget} from './action-card-widget';
import {QuestionnaireWidget} from './questionnaire';
import type {QuestionnaireDraft} from 'src/types/ask-ai';


interface WidgetRendererProps {
  part: WidgetPart;
  publishMessageAction?: (action: ActionEvent) => void;
  onResize?: () => void;
  // Questionnaire submit handler (provided when rendered in the dock).
  onQuestionnaireSubmit?: (qaText: string) => void;
  // Questionnaire wizard-progress persistence (survives minimize/maximize).
  getQuestionnaireDraft?: () => QuestionnaireDraft | null;
  onQuestionnaireDraftChange?: (draft: QuestionnaireDraft) => void;
  // Shared dock chrome handlers (questionnaire + action-card).
  onDockMinimize?: () => void;
  onDockClose?: () => void;
  dockDisabled?: boolean;
}

export function WidgetRenderer({
  part,
  publishMessageAction,
  onResize,
  onQuestionnaireSubmit,
  getQuestionnaireDraft,
  onQuestionnaireDraftChange,
  onDockMinimize,
  onDockClose,
  dockDisabled,
}: WidgetRendererProps) {
  const {data} = part;

  if (data.widget_type === 'list') {
    return (
      <ListWidget
        payload={data.payload}
        publishMessageAction={publishMessageAction}
        onResize={onResize}
      />
    );
  }

  if (data.widget_type === 'cta') {
    return (
      <CTAWidget
        data={data.payload as CTAWidgetPayload}
        publishMessageAction={publishMessageAction}
      />
    );
  }
  if (data.widget_type === 'prompt-list-widget') {
    return (
      <PromptListWidget
        {...data.payload}
        publishMessageAction={publishMessageAction}
      />
    );
  }
  if (data.widget_type === 'title-widget') {
    return <TitleWidget {...data.payload} />;
  }

  if (data.widget_type === 'preview-card') {
    return (
      <PreviewCardWidget
        data={data.payload}
        publishMessageAction={publishMessageAction}
      />
    );
  }

  if (data.widget_type === 'action-card') {
    return (
      <ActionCardWidget
        payload={data.payload}
        publishMessageAction={publishMessageAction}
        onMinimize={onDockMinimize ?? (() => undefined)}
        onClose={onDockClose ?? (() => undefined)}
        disabled={dockDisabled}
      />
    );
  }

  if (data.widget_type === 'questionnaire-widget') {
    return (
      <QuestionnaireWidget
        payload={data.payload}
        onSubmit={onQuestionnaireSubmit ?? (() => undefined)}
        onMinimize={onDockMinimize ?? (() => undefined)}
        onClose={onDockClose ?? (() => undefined)}
        disabled={dockDisabled}
        getInitialDraft={getQuestionnaireDraft}
        onDraftChange={onQuestionnaireDraftChange}
      />
    );
  }

  return null;
}
